<template>
  <div class="jl-page">
    <JobsListStickySearch
      :visible="showStickySearch"
      :filter.sync="filter"
      :course-options="courses"
      :location-options="locations"
      @search="submitFilter"
    />

    <b-alert
      variant="success"
      class="jl-toast"
      :class="alert_class"
      :show="showDismissibleAlert"
      dismissible
    >{{ success_msg }}</b-alert>

    <div class="container jl-page__container">
      <HcBreadcrumb>
        <li class="breadcrumb-item"><a href="/">Home</a></li>
        <li class="breadcrumb-item" :class="job_name === '' ? 'active' : ''">
          <a v-if="job_name !== '' && $route.query.redirect_from === 'fresher_jobs'" href="/jobs-for-freshers">Jobs for freshers</a>
          <a v-else-if="job_name !== ''" :href="jobsListPath()">Jobs</a>
          <span v-else-if="$route.query.redirect_from === 'fresher_jobs'">Jobs for freshers</span>
          <span v-else>Jobs</span>
        </li>
        <li class="breadcrumb-item active" v-if="job_name !== ''" aria-current="page">{{ job_name }}</li>
      </HcBreadcrumb>

      <div class="jl-search-card hp-hide-mobile hp-animate">
        <JobsListSearch
          :filter.sync="filter"
          :course-options="courses"
          :location-options="locations"
          @search="submitFilter"
          @reset="clearFilter"
        />
      </div>

      <div
        v-if="mobileSearchOpen"
        ref="mobileSearchRoot"
        class="jl-search-card jl-search-card--mobile hp-show-mobile"
      >
        <JobsListSearch
          :filter.sync="filter"
          :course-options="courses"
          :location-options="locations"
          @search="onMobileSearch"
          @reset="onMobileReset"
        />
      </div>

      <div v-else-if="mobileFilterTags.length" class="jl-mobile-search-bar hp-show-mobile">
        <p class="jl-mobile-search-bar__summary">
          <span
            v-for="(tag, index) in mobileFilterTags"
            :key="tag + index"
            class="jl-mobile-search-bar__tag"
          >{{ tag }}</span>
        </p>
        <button
          type="button"
          class="jl-mobile-search-bar__icon-btn jl-mobile-search-trigger"
          aria-label="Edit search"
          @click="openMobileSearch"
        >
          <v-icon size="28">{{ $icons.magnify }}</v-icon>
        </button>
      </div>

      <div class="jl-top-banner hp-animate" v-if="!filterd">
        <div class="jl-top-banner__row">
          <h1 class="jl-page-title">{{ pageTitle }}</h1>
          <button
            v-if="!mobileFilterTags.length && !mobileSearchOpen"
            type="button"
            class="jl-mobile-search-bar__icon-btn jl-mobile-search-trigger hp-show-mobile"
            aria-label="Search and filter jobs"
            @click="openMobileSearch"
          >
            <v-icon size="28">{{ $icons.magnify }}</v-icon>
          </button>
        </div>
        <p class="jl-page-subtitle" v-if="no_matching_jobs">
          We are always updating our listings! Explore opportunities below or
          <a href="/jobseeker/register">sign up for job alerts</a>.
        </p>
        <p class="jl-result-count" v-else-if="meta && !loading">
          {{ meta.total }} openings found
        </p>
      </div>

      <div class="jl-seo-block hp-animate" v-if="top_content">
        <div v-if="!top_readMoreActivated" v-html="top_content.slice(0, 1000)"></div>
        <div v-else v-html="top_content"></div>
        <button
          v-if="top_content.length > 1000 && !top_readMoreActivated"
          type="button"
          class="hp-btn hp-btn--ghost jl-read-more"
          @click="activateReadMore('top')"
        >Read more</button>
      </div>

      <div class="jl-layout">
        <main class="jl-main">
          <div v-if="loading" class="jl-results">
            <div class="jl-card jl-card--skeleton" v-for="n in 4" :key="n">
              <content-loader viewBox="0 0 700 100" primaryColor="#f1f5f9" secondaryColor="#e2e8f0">
                <rect x="0" y="0" rx="8" ry="8" width="48" height="48" />
                <rect x="64" y="8" rx="4" ry="4" width="240" height="16" />
                <rect x="64" y="32" rx="4" ry="4" width="160" height="12" />
                <rect x="64" y="52" rx="4" ry="4" width="320" height="10" />
              </content-loader>
            </div>
          </div>

          <template v-else>
            <h2 class="jl-results-title" v-if="filterd">
              <span>{{ pageTitle }}</span>
              <button
                v-if="!mobileFilterTags.length && !mobileSearchOpen"
                type="button"
                class="jl-mobile-search-bar__icon-btn jl-mobile-search-trigger hp-show-mobile"
                aria-label="Search and filter jobs"
                @click="openMobileSearch"
              >
                <v-icon size="28">{{ $icons.magnify }}</v-icon>
              </button>
            </h2>

            <div v-if="!jobs.length && !no_matching_jobs" class="jl-empty">
              <p><strong>No jobs match your search.</strong></p>
              <p>Try adjusting filters or browse latest openings.</p>
              <a v-if="!isLogin" href="/jobseeker/register" class="hp-btn hp-btn--outline">Register for alerts</a>
            </div>

            <div v-if="jobs.length" class="jl-results">
              <JobCardList
                v-for="job in jobs"
                :key="job.job_id"
                :job="job"
                :job-base-path="jobDetailBasePath"
                :applied="!!job.applied"
              />
            </div>

            <div v-if="no_matching_jobs" class="text-center mt-4">
              <a :href="jobsListPath()" class="hp-btn hp-btn--outline">Find more jobs</a>
            </div>

            <div class="text-center mt-4" v-if="jobs.length && meta && jobs.length < meta.total">
              <button type="button" class="hp-btn hp-btn--outline" @click="loadMore">Load more</button>
            </div>

            <JobsListSidebarEngage
              class="hp-show-mobile"
              mobile
              :posts="featuredPosts"
              :loading-posts="postsLoading"
            />
          </template>
        </main>

        <aside class="jl-sidebar hp-hide-mobile">
          <JobsListFilters
            :filter="filter"
            :jobrole="jobrole"
            :exp-options="exp"
            @set-role="handleSetRole"
            @set-exp="handleSetExp"
            @set-salary="handleSetSalary"
            @clear-filter="handleClearSidebarFilter"
          />
          <JobsListSidebarEngage
            :posts="featuredPosts"
            :loading-posts="postsLoading"
          />
        </aside>
      </div>

      <div class="jl-seo-block hp-animate" v-if="bottom_content">
        <div v-if="!bottom_readMoreActivated" v-html="bottom_content.slice(0, 1000)"></div>
        <div v-else v-html="bottom_content"></div>
        <button
          v-if="bottom_content.length > 1000 && !bottom_readMoreActivated"
          type="button"
          class="hp-btn hp-btn--ghost jl-read-more"
          @click="activateReadMore('bottom')"
        >Read more</button>
      </div>

      <section class="jl-faq hp-animate" v-if="faqs.length">
        <h2 class="jl-section-title">Frequently asked questions — {{ job_head }}</h2>
        <div class="jl-faq__list">
          <div class="jl-faq__item" v-for="(faq, index) in faqs" :key="index">
            <button
              type="button"
              class="jl-faq__question"
              :class="{ 'jl-faq__question--open': openFaq === index }"
              @click="openFaq = openFaq === index ? null : index"
            >
              <span>{{ faq.question }}</span>
              <span class="jl-faq__icon">{{ openFaq === index ? '−' : '+' }}</span>
            </button>
            <div class="jl-faq__answer" v-show="openFaq === index">
              <p>{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import JobsListSearch from '@/components/jobs/JobsListSearch.vue';
import JobsListStickySearch from '@/components/jobs/JobsListStickySearch.vue';
import JobsListFilters from '@/components/jobs/JobsListFilters.vue';
import JobsListSidebarEngage from '@/components/jobs/JobsListSidebarEngage.vue';
import JobCardList from '@/components/JobCardList.vue';
import HcBreadcrumb from '@/components/chrome/HcBreadcrumb.vue';
import jobListMixin from '@/mixins/jobListMixin';
import { ContentLoader } from 'vue-content-loader';

import '@/assets/hire.css';
import '@/assets/home-page.css';
import '@/assets/jobs-list-page.css';

export default {
  name: 'JobListPage',
  components: {
    JobsListSearch,
    JobsListStickySearch,
    JobsListFilters,
    JobsListSidebarEngage,
    JobCardList,
    HcBreadcrumb,
    ContentLoader,
  },
  mixins: [jobListMixin],
  metaInfo() {
    return this.jobListMetaInfo(this);
  },
  computed: {
    mobileFilterTags() {
      const tags = [];
      if (this.filter.search) tags.push(this.filter.search);
      if (this.filter.city_id && this.locations && this.locations.length) {
        const loc = this.locations.find(item => item.id === this.filter.city_id);
        if (loc) tags.push(loc.text);
      }
      if (this.filter.course_id && this.courses && this.courses.length) {
        const course = this.courses.find(item => item.id === this.filter.course_id);
        if (course) tags.push(course.text);
      }
      if (this.selectedRole) tags.push(this.selectedRole);
      return tags;
    },
  },
  data() {
    return {
      openFaq: null,
      showStickySearch: false,
      mobileSearchOpen: false,
      featuredPosts: [],
      postsLoading: true,
    };
  },
  mounted() {
    this.syncFilterLabels();
    this.fetchFeaturedPosts();
    window.addEventListener('scroll', this.onScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('click', this.handleMobileSearchOutside, true);
  },
  watch: {
    locations() {
      this.syncFilterLabels();
    },
    jobrole() {
      this.syncFilterLabels();
    },
    '$route.query'() {
      this.mobileSearchOpen = false;
      this.syncFilterLabels();
    },
    mobileSearchOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          document.addEventListener('click', this.handleMobileSearchOutside, true);
        });
      } else {
        document.removeEventListener('click', this.handleMobileSearchOutside, true);
      }
    },
  },
  methods: {
    fetchFeaturedPosts() {
      this.postsLoading = true;
      this.action.getPosts(5).then((res) => {
        const posts = (res && (res.posts || res.data || res.result))
          ? (res.posts || res.data || res.result)
          : [];
        this.featuredPosts = Array.isArray(posts) ? posts.slice(0, 2) : [];
      }).catch(() => {
        this.featuredPosts = [];
      }).finally(() => {
        this.postsLoading = false;
      });
    },
    onScroll() {
      if (!window.matchMedia('(max-width: 991px)').matches) {
        this.showStickySearch = false;
        return;
      }
      this.showStickySearch = window.scrollY > 120;
    },
    syncFilterLabels() {
      if (this.setHeader) this.setHeader();
    },
    openMobileSearch() {
      this.mobileSearchOpen = true;
      this.showStickySearch = false;
    },
    closeMobileSearch() {
      this.mobileSearchOpen = false;
    },
    handleMobileSearchOutside(e) {
      if (!this.mobileSearchOpen) return;
      if (e.target.closest('.jl-mobile-search-trigger')) return;
      if (e.target.closest('.v-menu__content')) return;
      const root = this.$refs.mobileSearchRoot;
      if (root && !root.contains(e.target)) {
        this.closeMobileSearch();
      }
    },
    onMobileSearch(e) {
      this.submitFilter(e);
      this.mobileSearchOpen = false;
    },
    onMobileReset(e) {
      this.clearFilter(e);
      this.mobileSearchOpen = false;
    },
  },
};
</script>
